import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Grid,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Key as KeyIcon,
  Close as CloseIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';
import { getBrandedTableHeadSx } from '../utils/colorUtils';

const FS = '0.8125rem';
const TITLE_SX = { fontSize: '1rem', fontWeight: 600, py: 1.5 };
const FIELD_SX = { '& .MuiInputBase-root': { fontSize: FS }, '& .MuiInputLabel-root': { fontSize: FS }, '& .MuiFormHelperText-root': { fontSize: '0.6875rem' } };
const BTN_SX = { textTransform: 'none', fontSize: FS };
const displayName = (u) => u?.fullName || u?.username || '';

const InternalOrgUsersDialog = ({ open, onClose, organization }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    phone: '',
    designation: '',
    role: 'ORG_VIEWER',
    password: ''
  });

  useEffect(() => {
    if (open && organization?.id) {
      fetchUsers();
    }
  }, [open, organization?.id]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getInternalOrgUsers(organization.id);
      setUsers(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username || '',
        email: user.email || '',
        fullName: user.fullName || '',
        phone: user.phone || '',
        designation: user.designation || '',
        role: user.role || 'ORG_VIEWER',
        password: ''
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        fullName: '',
        phone: '',
        designation: '',
        role: 'ORG_VIEWER',
        password: ''
      });
    }
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setSaveError(null);
    setEditDialogOpen(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      fullName: '',
      phone: '',
      designation: '',
      role: 'ORG_VIEWER',
      password: ''
    });
  };

  const handleSaveUser = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      if (editingUser) {
        await ApiService.updateInternalOrgUser(organization.id, editingUser.id, formData);
      } else {
        await ApiService.createInternalOrgUser(organization.id, formData);
      }
      handleCloseEditDialog();
      fetchUsers();
    } catch (err) {
      console.error('Failed to save user:', err);
      setSaveError(err.response?.data?.error || err.response?.data?.message || 'Failed to save user. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }
    try {
      await ApiService.deleteInternalOrgUser(organization.id, userId);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Failed to deactivate user. Please try again.');
    }
  };

  const handleOpenResetPassword = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setShowPassword(false);
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      await ApiService.resetInternalOrgUserPassword(organization.id, resetPasswordUser.id, newPassword);
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword('');
      alert('Password reset successfully');
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert('Failed to reset password. Please try again.');
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ ...TITLE_SX, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Portal Users — {organization?.name}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Users who can sign in to the customer portal for this organization
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenEditDialog()}
              size="small"
              sx={{ ...BTN_SX, height: 32, flexShrink: 0 }}
            >
              Add User
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" action={
              <Button size="small" onClick={fetchUsers}>Retry</Button>
            }>
              {error}
            </Alert>
          ) : users.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: FS, color: 'text.secondary' }}>No users found</Typography>
              <Typography variant="caption" color="text.secondary">
                Add users to allow them to access the customer portal
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead sx={getBrandedTableHeadSx(theme)}>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Typography sx={{ fontSize: FS, fontWeight: 500 }}>
                          {displayName(user)}
                        </Typography>
                        {user.fullName && (
                          <Typography sx={{ fontSize: '0.6875rem', color: 'text.secondary' }}>
                            @{user.username}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role === 'ORG_ADMIN' ? 'Admin' : 'Viewer'}
                          size="small"
                          color={user.role === 'ORG_ADMIN' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.active ? 'Active' : 'Inactive'}
                          size="small"
                          color={user.active ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEditDialog(user)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reset Password">
                          <IconButton size="small" onClick={() => handleOpenResetPassword(user)}>
                            <KeyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {user.active && (
                          <Tooltip title="Deactivate">
                            <IconButton size="small" onClick={() => handleDeleteUser(user.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={BTN_SX}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create User Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={TITLE_SX}>
          {editingUser ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSaveError(null)}>{saveError}</Alert>}
          <Grid container spacing={1.5} sx={FIELD_SX}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                disabled={!!editingUser}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={!!editingUser}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Full Name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Designation"
                value={formData.designation}
                onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <MenuItem value="ORG_ADMIN" sx={{ fontSize: FS }}>Admin (can accept/reject quotations)</MenuItem>
                  <MenuItem value="ORG_VIEWER" sx={{ fontSize: FS }}>Viewer (view only)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {!editingUser && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  helperText="Minimum 6 characters"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleCloseEditDialog} sx={BTN_SX}>Cancel</Button>
          <Button
            variant="contained"
            sx={BTN_SX}
            onClick={handleSaveUser}
            disabled={saving || !formData.username || !formData.email || !formData.fullName || (!editingUser && !formData.password)}
          >
            {saving ? <CircularProgress size={18} /> : (editingUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={TITLE_SX}>Reset Password</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: FS, mb: 1.5 }}>
            New password for <strong>{displayName(resetPasswordUser)}</strong>
            {resetPasswordUser?.email && (
              <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}> · {resetPasswordUser.email}</Typography>
            )}
          </Typography>
          <TextField
            fullWidth
            size="small"
            autoFocus
            label="New Password"
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newPassword.length >= 6) handleResetPassword(); }}
            helperText="Minimum 6 characters"
            sx={FIELD_SX}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                    {showPassword ? <HideIcon fontSize="small" /> : <ShowIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setResetPasswordDialogOpen(false)} sx={BTN_SX}>Cancel</Button>
          <Button variant="contained" sx={BTN_SX} onClick={handleResetPassword} disabled={!newPassword || newPassword.length < 6}>
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InternalOrgUsersDialog;
